/**
 * What a failed request means to the person in front of the screen (storefront-fix-spec §PR-4).
 *
 * The storefront used to know two states — loading and loaded — and every failure fell into the
 * second one: a delisted car was a blank screen, a 500 was an empty market, a dropped connection
 * was "no cars available to rent yet". This module is the one place that tells those apart, and it
 * is pure on purpose: the screens only pick copy by the kind it returns, and the kinds are checked
 * here in the node env (the app has no component-test setup).
 */

/** How a request failed, as far as the person needs to know. */
export type RequestFailureKind =
  /** 404: the thing asked for is not there any more (or never was). */
  | 'not-found'
  /**
   * The person is signed in to an account that holds no wallet yet, and what was asked for belongs
   * to a wallet — deals, rentals, purchases (api `WALLET_NOT_LINKED`, app#241). Nothing failed:
   * there is nothing to show until a wallet is linked, and the screen says how to link one.
   */
  | 'wallet-not-linked'
  /** The device has no network, or the request never reached the api at all. */
  | 'offline'
  /** The api accepted the connection and then did not answer in time. */
  | 'timeout'
  /** Everything else: 5xx, a body that is not JSON, a status nothing here expects. */
  | 'failed';

/**
 * What the axios interceptor rejects with. The api's error body is kept as it was — `message`,
 * `code`, `payload` — so every consumer that reads those keeps working; `status` and the axios
 * `code` (`ERR_NETWORK`, `ECONNABORTED`) are added because the interceptor used to throw them
 * away, and without them a 404 and a lost connection were the same string.
 */
export interface RequestFailure {
  message: string;
  /** HTTP status when the api answered at all. */
  status?: number;
  /** The api's own code on an error body, or the axios transport code when there was no answer. */
  code?: string;
  payload?: unknown;
  [key: string]: unknown;
}

/** The message shown for a failure that carries none of its own — kept as it always was. */
export const GENERIC_REQUEST_FAILURE = 'Something went wrong!';

/** axios' code for "no response at all" (DNS, refused, offline, CORS). */
export const NETWORK_ERROR_CODE = 'ERR_NETWORK';
/** axios' code for a request cut short by its own `timeout`. */
export const TIMEOUT_ERROR_CODE = 'ECONNABORTED';
/**
 * api's code for "this account has no linked wallet" on a wallet-scoped route: the guard resolves
 * an account session to the account's primary wallet, and there is none (`auth.guard.ts`).
 */
export const WALLET_NOT_LINKED_CODE = 'WALLET_NOT_LINKED';

/**
 * How long a public read (feed, listing) may take before the screen says "taking longer" instead
 * of spinning. Reads only: a write can legitimately wait on a chain, and cutting it off would not
 * undo what it started.
 */
export const READ_TIMEOUT_MS = 12_000;

/**
 * How long the splash may hold the whole app before it offers a way out. The check waits 20 s;
 * a person waits less.
 */
export const SPLASH_LIMIT_MS = 10_000;

interface AxiosLikeError {
  code?: string;
  message?: string;
  response?: { status?: number; data?: unknown };
}

/**
 * The rejection the interceptor produces from an axios error. A JSON error body travels as it is
 * (plus the status); a non-JSON body — Caddy's 502 page, an empty 504 — becomes the generic
 * message, since printing a gateway's HTML to a buyer was never the intent; no response at all
 * keeps the transport code so the screen can tell "offline" from "timed out".
 */
export const toRequestFailure = (error: AxiosLikeError | undefined | null): RequestFailure => {
  const response = error?.response;

  if (response) {
    const body = response.data;

    if (typeof body === 'object' && body !== null && !Array.isArray(body)) {
      const message = (body as { message?: unknown }).message;

      return {
        ...(body as Record<string, unknown>),
        message: typeof message === 'string' && message ? message : GENERIC_REQUEST_FAILURE,
        status: response.status,
      };
    }

    return { message: GENERIC_REQUEST_FAILURE, status: response.status };
  }

  return {
    message: GENERIC_REQUEST_FAILURE,
    code: typeof error?.code === 'string' && error.code ? error.code : NETWORK_ERROR_CODE,
  };
};

const statusOf = (err: unknown): number | undefined => {
  const status = (err as { status?: unknown } | null)?.status;

  return typeof status === 'number' ? status : undefined;
};

/**
 * Whether a failed `/me` means the token itself was refused — the only failure that ends a session.
 *
 * Both session hooks used to drop the session on *any* error of their `/me` query, and `/me` sits
 * under the api's auth throttle (20/min): a 429 in the middle of a walk, a 502 from the gateway or
 * a dropped connection all cleared the tokens exactly as an expired token would, and the buyer was
 * an anonym on the next screen with nothing to explain why (app#207). Only 401 and 403 are the
 * server's verdict on the token; everything else is a verdict on the moment, and the session
 * stays for the next request to decide.
 */
export const isTokenRejected = (err: unknown): boolean => {
  const status = statusOf(err);

  return status === 401 || status === 403;
};

const codeOf = (err: unknown): string | undefined => {
  const code = (err as { code?: unknown } | null)?.code;

  return typeof code === 'string' ? code : undefined;
};

/**
 * The kind of a failure. `online` is `navigator.onLine` from the caller: a request that never got
 * an answer while the device says it has no network is "offline" whatever axios called it.
 *
 * An account without a wallet is told by the api's code, not by its 403: the same status also
 * means "not yours" and "dealers only", and neither of those is answered by linking a wallet. It
 * used to fall through to `failed`, so a buyer who had just registered by email opened their deals
 * and read that something broke on our side (app#241).
 */
export const classifyRequestFailure = (err: unknown, online = true): RequestFailureKind => {
  const status = statusOf(err);

  if (status !== undefined && codeOf(err) === WALLET_NOT_LINKED_CODE) return 'wallet-not-linked';
  if (status === 404) return 'not-found';
  if (status !== undefined) return 'failed';

  const code = codeOf(err);

  if (code === TIMEOUT_ERROR_CODE) return 'timeout';
  if (!online || code === NETWORK_ERROR_CODE) return 'offline';

  return 'failed';
};

/**
 * The retry rule for every query in the app (`main.tsx`). A 4xx is a verdict — asking four times
 * whether a delisted car exists only delays telling the buyer it does not. A timed-out read has
 * already waited its full budget; a second one would double it. Everything else — 5xx, a dropped
 * connection — is asked once more, and no more: the screen has to reach its error state before the
 * person gives up on it.
 */
export const shouldRetryRequest = (failureCount: number, err: unknown): boolean => {
  const status = statusOf(err);

  if (status !== undefined && status >= 400 && status < 500) return false;
  if (codeOf(err) === TIMEOUT_ERROR_CODE) return false;

  return failureCount < 1;
};

/** The screen-level state of one query, before any "is the list empty" question. */
export type QueryScreenState = 'loading' | 'ready' | RequestFailureKind;

export interface QueryScreenInput {
  isLoading: boolean;
  /** A retry after a failure is a fetch with `status: 'error'` — not `isLoading`, but still loading. */
  isFetching: boolean;
  /** TanStack pauses a query instead of failing it while `navigator.onLine` is false. */
  isPaused: boolean;
  isError: boolean;
  error: unknown;
  hasData: boolean;
  online: boolean;
}

/**
 * Which of loading / an explained failure / ready a screen is in. Order matters: a query that is
 * paused for lack of network is neither loading nor errored in TanStack's terms — it sits with
 * `status: 'pending'` and no data, which is exactly the shape the old screens read as "empty".
 * Data that is already on the screen wins over a paused or failed refetch: the list stays and
 * comes back on its own when the network does (SF-H3, second half). Without data, any fetch in
 * flight is loading — including the one "Try again" starts after a failure, which TanStack
 * reports with `status: 'error'` and `isLoading: false`.
 */
export const queryScreenState = (input: QueryScreenInput): QueryScreenState => {
  if (input.hasData) return 'ready';
  if (input.isLoading || input.isFetching) return 'loading';
  if (input.isPaused) return 'offline';
  if (input.isError) return classifyRequestFailure(input.error, input.online);

  return 'ready';
};

/**
 * One screen fed by several queries (app#210 — the buyer's purchases are escrow deals *and* Buy
 * Now purchases). It is loading while any of them is, and failed when any of them failed: a list
 * that quietly dropped its failed half would tell a buyer with only Buy Now purchases that they
 * have none. The first failure in argument order names the kind.
 */
export const combinedScreenState = (...states: QueryScreenState[]): QueryScreenState => {
  if (states.includes('loading')) return 'loading';

  return states.find((state) => state !== 'ready') ?? 'ready';
};

/** Whether the person has narrowed the feed — an empty result then means "nothing matched". */
export const hasActiveFilter = (filter: Record<string, unknown> | undefined | null): boolean =>
  !!filter && Object.values(filter).some((value) => value !== undefined && value !== '');
